"use client";

import { useState, useEffect, useRef, useId } from "react";
import { unsplash } from "@/lib/unsplash";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { trackSearch } from "@/lib/analytics";
import { products, type Product } from "@/lib/products";
import { SmartImage } from "@/components/SmartImage";
import { buildSearchUrl } from "@/lib/filters";

type SpeechRecognitionInstance = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  onresult: ((e: { results: { transcript: string }[][] }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};

function getSpeechRecognition(): (new () => SpeechRecognitionInstance) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition as new () => SpeechRecognitionInstance) ??
    (w.webkitSpeechRecognition as new () => SpeechRecognitionInstance) ??
    null;
}

export function SearchBar() {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  // SSR ile aynı HTML için false başlar; tarayıcı desteği mount sonrası
  // belirlenir (aksi halde hydration mismatch: server'da buton yok, client'ta var).
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const router = useRouter();
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recogRef = useRef<SpeechRecognitionInstance | null>(null);
  const inputId = useId();
  const listId = useId();

  useEffect(() => {
    // Tarayıcı yeteneği dış sistemdir — mount sonrası tek seferlik senkronizasyon
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVoiceSupported(getSpeechRecognition() !== null);
    return () => {
      try {
        recogRef.current?.stop();
      } catch {
        /* yok say */
      }
    };
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!q.trim() || q.trim().length < 2) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(() => {
      const norm = (s: string) => s.toLocaleLowerCase("tr");
      const needle = norm(q.trim());
      const matches = products
        .filter((p) => norm(p.name).includes(needle) || norm(p.category).includes(needle) || norm(p.description).includes(needle))
        .slice(0, 5);
      setSuggestions(matches);
      setOpen(true);
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  const go = (term: string) => {
    const t = term.trim().slice(0, 100);
    if (!t) return;
    trackSearch(t);
    setOpen(false);
    stopVoice();
    router.push(buildSearchUrl(t));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    go(q);
  };

  const stopVoice = () => {
    try {
      recogRef.current?.stop();
    } catch {
      /* yok say */
    }
    setListening(false);
  };

  const toggleVoice = () => {
    if (listening) {
      stopVoice();
      return;
    }
    const SR = getSpeechRecognition();
    if (!SR) {
      setVoiceError("Tarayıcın sesli aramayı desteklemiyor — Chrome veya Edge ile dene.");
      return;
    }
    setVoiceError(null);
    try {
      const recog = new SR();
      recogRef.current = recog;
      recog.lang = "tr-TR";
      recog.interimResults = false;
      recog.maxAlternatives = 1;
      recog.onresult = (e) => {
        const transcript = e.results?.[0]?.[0]?.transcript?.trim();
        if (transcript) {
          setQ(transcript);
          go(transcript);
        }
      };
      recog.onerror = (e) => {
        setListening(false);
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          setVoiceError("Mikrofon izni gerekli — adres çubuğundaki kilit simgesinden izin ver.");
        } else if (e.error === "no-speech") {
          setVoiceError("Ses algılanamadı — tekrar dene.");
        } else {
          setVoiceError("Sesli arama başlatılamadı — yazarak aramayı dene.");
        }
      };
      recog.onend = () => setListening(false);
      recog.start();
      setListening(true);
    } catch {
      setListening(false);
      setVoiceError("Sesli arama başlatılamadı — yazarak aramayı dene.");
    }
  };

  return (
    <form onSubmit={handleSubmit} action="/arama" method="get" className="relative flex w-full items-center" role="search" aria-label="Ürün arama">
      <label htmlFor={inputId} className="sr-only">
        Qnail Professional ürünlerde ara
      </label>
      <div className={`flex h-11 w-full items-center gap-2 rounded-2xl bg-White px-4 py-3 outline outline-1 outline-offset-[-1px] outline-Background-dark transition focus-within:outline-2 focus-within:outline-Primary ${listening ? "outline-2 outline-Tertiary" : ""}`}>
        <button type="submit" aria-label="Ara" className="flex shrink-0 items-center justify-center text-Primary-dark transition hover:text-Primary active:scale-95">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
        <input
          id={inputId}
          name="q"
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setVoiceError(null);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Protez tırnak, kalıcı oje tırnak ürünlerini ara..."
          autoComplete="off"
          enterKeyHint="search"
          inputMode="search"
          maxLength={100}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          className="w-full min-w-0 flex-1 bg-transparent text-sm leading-4 text-zinc-900 placeholder:text-500 focus:outline-none [&::-webkit-search-cancel-button]:cursor-pointer"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            aria-label="Aramayı temizle"
            className="flex shrink-0 items-center justify-center rounded-full px-1 text-400 transition hover:text-800 active:scale-95"
          >
            <span aria-hidden="true" className="text-base leading-none">✕</span>
          </button>
        )}
        {voiceSupported && (
          <button
            type="button"
            onClick={toggleVoice}
            aria-label={listening ? "Sesli aramayı durdur" : "Sesli arama başlat"}
            aria-pressed={listening}
            title={listening ? "Dinleniyor — durdurmak için bas" : "Sesli arama"}
            className={`relative flex shrink-0 items-center justify-center rounded-full p-1 transition active:scale-95 ${listening ? "text-Tertiary" : "text-300 hover:text-Primary"}`}
          >
            {listening && (
              <span aria-hidden="true" className="absolute inset-0 animate-ping rounded-full bg-Tertiary/30" />
            )}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="relative h-5 w-5" aria-hidden="true">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <line x1="12" y1="17" x2="12" y2="22" />
              <line x1="9" y1="22" x2="15" y2="22" />
            </svg>
          </button>
        )}
      </div>
      <span aria-live="polite" className="sr-only">
        {listening ? "Dinleniyor, konuşmaya başlayabilirsin" : ""}
        {voiceError ?? ""}
      </span>
      {voiceError && open === false && (
        <p role="alert" className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border bg-white px-4 py-2 text-xs text-800 shadow-xl">
          {voiceError}
        </p>
      )}
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border bg-white shadow-xl sm:w-80">
          {suggestions.length === 0 ? (
            <p className="px-4 py-3 text-[13px] text-zinc-500">
              Sonuç yok — <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => go(q)} className="font-medium text-[var(--brand)] hover:underline">“{q.trim().slice(0, 50)}” ile ara →</button>
            </p>
          ) : (
            <ul id={listId} role="listbox" aria-label="Arama önerileri">
              {suggestions.map((p) => (
                <li key={p.slug} role="option" aria-selected="false">
                  <Link
                    href={`/urunler/${p.slug}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      trackSearch(p.name);
                      setOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 transition hover:bg-zinc-50"
                  >
                    <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
                      <SmartImage src={p.image} fallback={unsplash.nails(200, 200)} alt={p.name} fill className="object-cover" sizes="44px" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{p.name}</span>
                      <span className="block text-xs text-zinc-500">{p.category}</span>
                    </span>
                    <span className="shrink-0 text-sm font-bold">{p.price.toFixed(2)} ₺</span>
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(q)}
                  className="flex w-full items-center justify-center gap-1.5 bg-zinc-50 px-4 py-2.5 text-[13px] font-medium transition hover:bg-zinc-100"
                >
                  Tüm sonuçları gör <span aria-hidden="true">→</span>
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </form>
  );
}
